import React from 'react';
import styled, { keyframes } from 'styled-components';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import { ArrowLeft, Megaphone } from 'lucide-react';
import currency from 'helpers/currency';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import breakpoints from 'theme/breakpoints';

export default function CampaignsSection({ data, loading, onBack }) {
    const resumo = data?.resumo;
    const campanhas = data?.campanhas || [];
    const hasRows = campanhas.length > 0;

    return (
        <Wrapper>
            <BackRow
                onClick={onBack}
                role="button"
                tabIndex={0}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onBack()}
            >
                <BackIconWrap>
                    <ArrowLeft size={18} color={colors.secondaryPurple} />
                </BackIconWrap>
                <BackLabel>Voltar</BackLabel>
            </BackRow>

            <SectionCard>
                <SectionHeader>
                    <IconBadge>
                        <Megaphone size={20} color={colors.occasionalPurple} />
                    </IconBadge>
                    <HeaderText>
                        <SectionTitle>Campanhas</SectionTitle>
                        <SectionDescription>
                            De onde vieram os compradores do evento, conforme os parâmetros UTM do
                            link que trouxe cada visita.
                        </SectionDescription>
                    </HeaderText>
                </SectionHeader>

                <Divider />

                {loading ? (
                    <LoaderWrap>
                        <Loader />
                    </LoaderWrap>
                ) : !hasRows ? (
                    <EmptyState>
                        <EmptyIcon>
                            <Megaphone size={28} color={colors.nightLight} />
                        </EmptyIcon>
                        <Text color={colors.night} textAlign='center' paddingBottom={0} name='small'>
                            Nenhum pedido registrado ainda.
                        </Text>
                        <EmptyHint>
                            Divulgue o evento com links marcados (utm_source, utm_medium e
                            utm_campaign) para ver aqui qual campanha traz venda.
                        </EmptyHint>
                    </EmptyState>
                ) : (
                    <>
                        <SummaryGrid>
                            <SummaryCard>
                                <SummaryLabel>Pedidos iniciados</SummaryLabel>
                                <SummaryValue>{resumo?.pedidos ?? 0}</SummaryValue>
                            </SummaryCard>
                            <SummaryCard>
                                <SummaryLabel>Vendas aprovadas</SummaryLabel>
                                <SummaryValue>{resumo?.vendas ?? 0}</SummaryValue>
                            </SummaryCard>
                            <SummaryCard>
                                <SummaryLabel>Receita</SummaryLabel>
                                <SummaryValue>{currency.format(resumo?.receita ?? 0)}</SummaryValue>
                            </SummaryCard>
                            <SummaryCard>
                                <SummaryLabel>Vinda de campanha</SummaryLabel>
                                <SummaryValue>{resumo?.participacaoAtribuida ?? 0}%</SummaryValue>
                                <SummaryHint>{currency.format(resumo?.receitaAtribuida ?? 0)}</SummaryHint>
                            </SummaryCard>
                        </SummaryGrid>

                        <TableScroll>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th>Origem</Th>
                                        <Th>Mídia</Th>
                                        <Th>Campanha</Th>
                                        <Th align='right'>Pedidos</Th>
                                        <Th align='right'>Vendas</Th>
                                        <Th align='right'>Conversão</Th>
                                        <Th align='right'>Ticket médio</Th>
                                        <Th align='right'>Receita</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campanhas.map((row, index) => (
                                        <tr key={`${row.origem}-${row.midia}-${row.campanha}-${index}`}>
                                            <Td>
                                                <Origin muted={!row.atribuido}>{row.origem}</Origin>
                                            </Td>
                                            <Td>{row.midia || '—'}</Td>
                                            <Td>{row.campanha || '—'}</Td>
                                            <Td align='right'>{row.pedidos}</Td>
                                            <Td align='right'>{row.vendas}</Td>
                                            <Td align='right'>{row.taxaConversao}%</Td>
                                            <Td align='right'>{currency.format(row.ticketMedio)}</Td>
                                            <Td align='right'>{currency.format(row.receita)}</Td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </TableScroll>
                    </>
                )}
            </SectionCard>
        </Wrapper>
    );
}

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px); }
    to { opacity: 1; transform: translateX(0); }
`;

const Wrapper = styled.div`
    animation: ${slideIn} 0.25s ease-out;
`;

const BackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 12px 6px 6px;
    margin-bottom: 16px;
    width: fit-content;
    border-radius: 10px;
    outline: none;
    transition: background 0.15s ease;

    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { box-shadow: 0 0 0 2px ${colors.secondaryPurple}44; }
`;

const BackIconWrap = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BackLabel = styled.span`
    font-size: ${fonts.sizes.default};
    font-weight: 500;
    color: ${colors.nightDark};
`;

const SectionCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 24px;
    border: 1px solid ${colors.night}11;
    box-shadow: 0 1px 8px ${colors.night}08;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const IconBadge = styled.div`
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 10px;
    background: ${colors.occasionalPurple}12;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const SectionDescription = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const Divider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 20px 0;
`;

const LoaderWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 32px 0;
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 20px;

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(4, 1fr);
    }
`;

const SummaryCard = styled.div`
    border: 1px solid ${colors.night}11;
    border-radius: 10px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SummaryLabel = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;

const SummaryValue = styled.strong`
    font-size: ${fonts.sizes.subtitle};
    color: ${colors.nightDark};
`;

const SummaryHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
`;

/* A tabela tem oito colunas e não cabe num celular: o scroll fica nela, e não
   no corpo da página, para o resto do painel continuar alinhado. */
const TableScroll = styled.div`
    width: 100%;
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
`;

const Th = styled.th`
    text-align: ${({ align }) => align || 'left'};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    padding: 10px 12px;
    border-bottom: 1px solid ${colors.night}11;
    white-space: nowrap;
`;

const Td = styled.td`
    text-align: ${({ align }) => align || 'left'};
    font-size: ${fonts.sizes.small};
    color: ${colors.nightDark};
    padding: 10px 12px;
    border-bottom: 1px solid ${colors.night}0A;
    white-space: nowrap;
`;

const Origin = styled.span`
    font-weight: 500;
    color: ${({ muted }) => (muted ? colors.nightLight : colors.nightDark)};
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 32px 16px;
`;

const EmptyIcon = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${colors.neutralDark};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const EmptyHint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.nightLight};
    text-align: center;
    max-width: 420px;
`;
